import { fromJS } from 'immutable';
import {
  FIELD_NAMES,
  GDPR_SETTING,
  LEGACY_CONSENT_FIELD_NAMES,
  EXPLICIT_CONSENT_FIELD_NAMES,
  LEGACY_GDPR_SETTINGS,
  OPTIN_RADIO_DEFAULTS,
  OPTIN_FORMATS,
  DISCLAIMER_LOCATION_TYPES,
  CREDENTIAL_TYPES,
} from 'utils/constants';
import { errorTextImpliedHasInvalidField, ERROR_TEXT_CHECKED_FIELD_NO_TEXT } from 'langs/en/errors';
import { makeImmutable } from 'utils/immutableUtils';
import { standardFieldNameToTaxiFieldName } from 'utils/coercionUtils';
import ConditionalityGraph from 'utils/conditionality.graph';
import { generateInvalidFieldData } from 'utils/editorDefaultStates';
import { createUIState } from './ops.ui';
import { updateFieldOverrideState, updateFormValidity } from './ops.field';
import { addLegacyConditionToPhone, removeLegacyConditionFromPhone } from './ops.conditionality';

export function setForm(state, form, shouldRebuildCollapsedState) {
  let fieldOrderCollapsedState = state.getIn(['meta', 'uiState', 'fieldOrderCollapsedState']) && state.getIn(['meta', 'uiState', 'fieldOrderCollapsedState']).toJS();
  const visibleBranches = state.getIn(['meta', 'uiState', 'visibleBranches']);
  if (shouldRebuildCollapsedState) {
    fieldOrderCollapsedState = Array(form.fieldOrder.length).fill(false);
  }
  const graph = new ConditionalityGraph({});
  const fields = Object.values(form.fields)
    .map((field) => ({ id: field.id, condition: field.condition, name: field.name }));
  const stateToFieldIdMap = fields.reduce((memo, field) => {
    memo[`state.${standardFieldNameToTaxiFieldName(field.name)}`] = field.id; // eslint-disable-line no-param-reassign
    return memo;
  }, {});
  fields.forEach((field) => {
    if (field.condition) {
      const conditionalityArr = field.condition.split(' ');
      const conditionalAction = conditionalityArr.slice(0, 2).join(' ');
      const fieldVertex = graph.findOrCreateVertex({ left: field.id, action: conditionalAction });
      graph.importConditionString(fieldVertex, conditionalityArr.slice(2).join(' '), stateToFieldIdMap);
      form.fields[field.id].condition = ''; // eslint-disable-line no-param-reassign
    } else {
      const fieldVertex = graph.findOrCreateVertex({ left: field.id, action: 'render if' });
      graph.addPath([0, fieldVertex], fieldVertex);
    }
  });
  if (form.fieldConditionalityInfo && form.fieldConditionalityInfo.branchingFields) {
    Object.keys(form.fieldConditionalityInfo.branchingFields).forEach((fieldId) => {
      graph.setUserBranches(form.fields[fieldId].id, form.fieldConditionalityInfo.branchingFields[fieldId]);
    });
  }

  const formUIState = makeImmutable(createUIState(form, fieldOrderCollapsedState, visibleBranches));
  return state
    .set('form', makeImmutable(form))
    .setIn(['form', 'fieldConditionalityInfo', 'dehydratedGraph'], makeImmutable(graph.dehydrate()))
    .setIn(['meta', 'uiState'], formUIState)
    .deleteIn(['meta', 'stashedOverrides'])
    .setIn(['meta', 'invalidFormData'], makeImmutable(generateInvalidFieldData(form.fields)));
}

/**
 * Maps PASS_CONSENT_FOR_ALL_USERS to the appropriate GDPR setting based on credential type.
 * For Degree: maps to GEO_IMPLIED
 * For Short Course: maps to ALWAYS
 * For other types: returns the consentStyle unchanged
 *
 * Can accept either state or credentialType string
 */
export function mapConsentStyleByCredentialType(stateOrCredentialType, consentStyle) {
  if (consentStyle !== GDPR_SETTING.PASS_CONSENT_FOR_ALL_USERS) {
    return consentStyle;
  }

  let credentialType;

  // Handle both state object and string credentialType
  if (typeof stateOrCredentialType === 'string') {
    credentialType = stateOrCredentialType;
  } else {
    credentialType = stateOrCredentialType.getIn(['meta', 'availableCredentialTypes', 0, 'value']);
  }

  if (credentialType === CREDENTIAL_TYPES.DEGREE) {
    return GDPR_SETTING.GEO_IMPLIED;
  }

  if (credentialType === CREDENTIAL_TYPES.SHORT_COURSE) {
    return GDPR_SETTING.ALWAYS;
  }

  return consentStyle;
}

function validateDisclaimerOverride(state, consentStyle) {
  const overrideKeyPathForType = (disclaimerType) => ['syntheticContentEntities', 'disclaimers', disclaimerType, 'override'];
  const clearOverrideValidation = (stateToClear, disclaimerType) => updateFormValidity(stateToClear, overrideKeyPathForType(disclaimerType), true).mergeIn(['meta', 'uiState', ...overrideKeyPathForType(disclaimerType)], {
    isValid: true,
    errorDisplay: '',
    className: '',
  });

  if (![GDPR_SETTING.GEO_IMPLIED, GDPR_SETTING.PASS_CONSENT_FOR_ALL_USERS].includes(consentStyle)) {
    return Object.values(DISCLAIMER_LOCATION_TYPES)
      .reduce((newState, disclaimerType) => clearOverrideValidation(newState, disclaimerType), state);
  }

  return Object.values(DISCLAIMER_LOCATION_TYPES)
    .reduce((newState, disclaimerType) => {
      const disclaimer = newState.getIn(['form', 'syntheticContentEntities', 'disclaimers', disclaimerType]);
      const show = disclaimer?.get('show');
      if (!show) {
        return clearOverrideValidation(newState, disclaimerType);
      }

      const value = disclaimer?.get('override');
      const isInvalid = !value || value.trim().length === 0;

      if (!isInvalid) {
        return newState;
      }

      return updateFormValidity(newState, overrideKeyPathForType(disclaimerType), false, ERROR_TEXT_CHECKED_FIELD_NO_TEXT, true);
    }, state);
}

const GEO_IMPLIED_STYLES = [GDPR_SETTING.GEO_IMPLIED, GDPR_SETTING.PASS_CONSENT_FOR_ALL_USERS];

export function changeConsentStyle(state, consentStyle) {
  // Map PASS_CONSENT_FOR_ALL_USERS to the appropriate setting based on credential type
  const mappedConsentStyle = mapConsentStyleByCredentialType(state, consentStyle);

  const consentStyleKeypath = ['form', 'syntheticContentEntities', 'gdprSetting'];
  const oldConsentStyle = state.getIn(consentStyleKeypath);
  const consentRestricted = [GDPR_SETTING.ALWAYS_IMPLIED, GDPR_SETTING.ALWAYS].includes(mappedConsentStyle);
  const usesImpliedConsent = [GDPR_SETTING.ALWAYS_IMPLIED, GDPR_SETTING.GEO_IMPLIED, GDPR_SETTING.PASS_CONSENT_FOR_ALL_USERS].includes(mappedConsentStyle);

  let newState = state;
  const fields = Object.values(newState.getIn(['form', 'fields']).toJS());
  fields.forEach((field) => {
    if (field.delete) return;

    if (field.name === FIELD_NAMES.LEAD_SHARE_OPT_IN) {
      const [newFormat, defaultKeypath, newDefault] = consentRestricted ? [OPTIN_FORMATS.RADIO, 'defaultRadio', OPTIN_RADIO_DEFAULTS.NONE] : [OPTIN_FORMATS.CHECKBOX, 'defaultChecked', false]; // defaultChecked set to false to keep behavior consistent between new form creation and consent style changes.
      newState = newState.setIn(['form', 'fields', field.id, 'editors', 'optInData', 'format'], newFormat)
        .setIn(['form', 'fields', field.id, 'editors', 'optInData', defaultKeypath], newDefault);
    }
    const credentialType = state.getIn(['meta', 'availableCredentialTypes', 0, 'value']);
    const keypath = ['fields', field.id, 'name'];
    let errorMessage;
    let valid = true;
    if ((LEGACY_CONSENT_FIELD_NAMES.includes(field.name) && usesImpliedConsent) || (EXPLICIT_CONSENT_FIELD_NAMES.includes(field.name) && mappedConsentStyle === GDPR_SETTING.ALWAYS_IMPLIED)) {
      valid = false;
      errorMessage = errorTextImpliedHasInvalidField(field.name, credentialType);
    }
    newState = updateFormValidity(newState, keypath, valid, errorMessage);

    if (field.name === FIELD_NAMES.PHONE) {
      // Handle phone field for all implied consent options
      if ([GDPR_SETTING.ALWAYS_IMPLIED, GDPR_SETTING.GEO_IMPLIED, GDPR_SETTING.PASS_CONSENT_FOR_ALL_USERS].includes(mappedConsentStyle)) {
        newState = newState.setIn(['form', 'fields', field.id, 'required'], false);
        newState = newState.setIn(['form', 'fields', field.id, 'hidden'], false);
      }

      const oldConsentIsLegacy = LEGACY_GDPR_SETTINGS.includes(oldConsentStyle);
      const newConsentIsLegacy = LEGACY_GDPR_SETTINGS.includes(mappedConsentStyle);
      if (oldConsentIsLegacy !== newConsentIsLegacy) {
        const graph = new ConditionalityGraph(state.getIn(['form', 'fieldConditionalityInfo', 'dehydratedGraph']).toJS());
        if (newConsentIsLegacy) {
          addLegacyConditionToPhone(field.id, graph);
        } else {
          removeLegacyConditionFromPhone(field.id, graph);
        }
        newState = newState.setIn(['form', 'fieldConditionalityInfo', 'dehydratedGraph'], fromJS(graph.dehydrate()));
      }
    }

    // Initialize/Update GDPR overrides for phone and email
    if (usesImpliedConsent && [FIELD_NAMES.PHONE, FIELD_NAMES.EMAIL].includes(field.name)) {
      // Synchronize includeImpliedConsent for base field
      if (field.editors && field.editors.phoneConfig) {
        newState = newState.setIn(['form', 'fields', field.id, 'editors', 'phoneConfig', 'includeImpliedConsent'], true);
      }
      if (field.editors && field.editors.emailConfig) {
        newState = newState.setIn(['form', 'fields', field.id, 'editors', 'emailConfig', 'includeImpliedConsent'], true);
      }

      // Update GDPR overrides
      if (field.overrides) {
        Object.keys(field.overrides).forEach((overrideKey) => {
          const override = field.overrides[overrideKey];
          if (override && override.editors) {
            // Phone override: Set required to false
            if (field.name === FIELD_NAMES.PHONE && override.editors.phoneConfig) {
              newState = newState.setIn(['form', 'fields', field.id, 'overrides', overrideKey, 'required'], false);
              newState = newState.setIn(['form', 'fields', field.id, 'overrides', overrideKey, 'editors', 'phoneConfig', 'includeImpliedConsent'], true);
            }
            // Email override: Keep default but ensure includeImpliedConsent is true
            if (field.name === FIELD_NAMES.EMAIL && override.editors.emailConfig) {
              newState = newState.setIn(['form', 'fields', field.id, 'overrides', overrideKey, 'editors', 'emailConfig', 'includeImpliedConsent'], true);
            }
          }
        });
      }
    }

    const stashPath = ['meta', 'stashedOverrides', field.id];
    const currentOverride = newState.getIn(['form', 'fields', field.id, 'overrides', 'gdpr']);
    if (oldConsentStyle && oldConsentStyle !== consentStyle && currentOverride && !currentOverride.get('delete')) {
      const stashEntry = fromJS({ type: field.type, name: field.name, override: currentOverride });
      newState = newState.setIn([...stashPath, oldConsentStyle], stashEntry);
      // Both geo-implied styles share the same overrides, so carry them over.
      if (GEO_IMPLIED_STYLES.includes(oldConsentStyle) && GEO_IMPLIED_STYLES.includes(consentStyle)) {
        newState = newState.setIn([...stashPath, consentStyle], stashEntry);
      }
    }

    newState = updateFieldOverrideState(newState, field.id, field.type, consentStyle);

    // Restore overrides the user entered the last time this consent style was active.
    const stashed = newState.getIn([...stashPath, consentStyle]);
    if (stashed && stashed.get('type') === field.type && stashed.get('name') === field.name && newState.hasIn(['form', 'fields', field.id, 'overrides', 'gdpr'])) {
      const currentId = newState.getIn(['form', 'fields', field.id, 'overrides', 'gdpr', 'id']);
      let restored = stashed.get('override').delete('delete');
      restored = currentId ? restored.set('id', currentId) : restored.delete('id');
      newState = newState.setIn(['form', 'fields', field.id, 'overrides', 'gdpr'], restored);
      const restoredHelpText = restored.getIn(['helpText', 'value']);
      if (typeof restoredHelpText === 'string' && restoredHelpText.trim().length) {
        newState = updateFormValidity(newState, ['fields', field.id, 'overrides', 'gdpr', 'helpText', 'value'], true);
      }
    }
    if (stashed) {
      newState = newState.deleteIn([...stashPath, consentStyle]);
    }
  });

  newState = newState.setIn(consentStyleKeypath, consentStyle);
  const impliedConsent = !(LEGACY_GDPR_SETTINGS.includes(mappedConsentStyle));
  newState = newState.setIn(['form', 'formMetadata', 'formAttributesConfig', 'impliedConsent'], impliedConsent);

  // Map GDPR setting to send_consent_for_all_users flag
  // PASS_CONSENT_FOR_ALL_USERS (send_consent_for_all_users: true) - send consent to all users
  let sendConsentForAllUsers = false;
  if (consentStyle === GDPR_SETTING.PASS_CONSENT_FOR_ALL_USERS) {
    sendConsentForAllUsers = true;
  }
  newState = newState.setIn(['form', 'formMetadata', 'formAttributesConfig', 'send_consent_for_all_users'], sendConsentForAllUsers);

  newState = validateDisclaimerOverride(newState, mappedConsentStyle);
  return newState;
}
